import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import {
  Keyboard,
  Platform,
  TextInput as RnTextInput,
  StyleProp,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';

import { IconClose, Pressable, Text, createMqStyles, tokens, useMqSelect } from '@fhs/ui';
import { useDebouncedCallback } from '@fhs/utils';
import useLocationPredictions from '@fhs-legacy/frontend/src/hooks/geolocation/use-location-predictions';
import {
  focusStoreLocatorFirstRestaurantResult,
  requestStoreLocatorFirstRestaurantFocus,
} from '@fhs-legacy/frontend/src/pages/store-locator/store-locator-focus-handoff';
import {
  CustomEventNames,
  EventTypes,
  useCRMEventsContext,
} from '@fhs-legacy/frontend/src/state/crm-events';
import { useGeolocation } from '@fhs-legacy/frontend/src/state/geolocation';
import { expandUSStateAbbreviationsInAddress } from '@fhs-legacy/frontend/src/utils/restaurant';
import {
  STORE_LOCATOR_SEARCH_INPUT,
  STORE_LOCATOR_SUGGESTION_AT,
} from '@fhs-legacy/frontend/src/utils/test-ids';

/** Google only returns useful predictions once the query is specific enough. */
const MIN_QUERY_LENGTH = 3;
const PREDICTIONS_DEBOUNCE_MS = 300;
const MAX_PREDICTIONS_MOBILE = 3;
const MAX_PREDICTIONS_DESKTOP = 5;

type Prediction = ReturnType<typeof useLocationPredictions>['predictions'][number];

export interface LocationSearchBarHandle {
  focus: () => void;
  blur: () => void;
  /** Empties the field and closes predictions — used when locate-me replaces a searched location. */
  clear: () => void;
}

export interface LocationSearchBarProps {
  /** Placeholder copy. Defaults to the shared "enter a location" prompt. */
  placeholder?: string;
  autoFocus?: boolean;
  /** `nativeID` of a visible heading that labels this field. */
  addressHeadingNativeId?: string;
  style?: StyleProp<ViewStyle>;
  /** Styles the bordered box around the input, e.g. to reserve room for an adjacent control. */
  inputContainerStyle?: StyleProp<ViewStyle>;
  inputStyle?: StyleProp<TextStyle>;
  /** Styles the predictions panel, e.g. to let it run wider than the input it hangs from. */
  dropdownStyle?: StyleProp<ViewStyle>;
  onFocus?: () => void;
  /** Receives the current value so callers can react to an empty field losing focus. */
  onBlur?: (value: string) => void;
}

/**
 * Store locator address field: a text input backed by Google Places predictions.
 *
 * Selecting a prediction is what moves the map — it writes the manual coordinates on the shared
 * geolocation state, which the locator reads to refetch restaurants.
 */
export const LocationSearchBar = forwardRef<LocationSearchBarHandle, LocationSearchBarProps>(
  function LocationSearchBar(
    {
      placeholder,
      autoFocus,
      addressHeadingNativeId,
      style,
      inputContainerStyle,
      inputStyle,
      dropdownStyle,
      onFocus,
      onBlur,
    },
    forwardedRef
  ) {
    const styles = useLocationSearchBarMqStyles();
    const { formatMessage } = useIntl();
    const { logRBIEvent } = useCRMEventsContext();
    const { manualAddress, setManualAddress, updateManualCoordinates } = useGeolocation();

    const inputRef = useRef<RnTextInput | null>(null);
    const [value, setValue] = useState(manualAddress);
    const [isFocused, setIsFocused] = useState(false);
    // `onBlur` can fire with a stale closure — a press inside the field blurs the input before
    // React has re-rendered with the value that press produced.
    const valueRef = useRef(value);
    // A picked prediction fills the field with its own description, which would otherwise
    // immediately re-open the list with that exact address as the only match.
    const [hasPickedPrediction, setHasPickedPrediction] = useState(Boolean(manualAddress));
    // Deliberately not seeded from `manualAddress`: mounting with a known address would spend a
    // Places request re-predicting an address the guest already chose.
    const [query, setQuery] = useState('');

    // Every keystroke would otherwise cost a Places request.
    const debounceQuery = useDebouncedCallback(setQuery, PREDICTIONS_DEBOUNCE_MS);
    const cancelPredictionsRef = useRef<(() => void) | undefined>();
    const { isLoading: isLoadingPredictions, predictions: locationPredictions } =
      useLocationPredictions(query);
    const maxPredictions = useMqSelect(
      { $gteDesktop: MAX_PREDICTIONS_DESKTOP },
      MAX_PREDICTIONS_MOBILE
    );
    const predictions = locationPredictions.slice(0, maxPredictions);

    const setFieldValue = useCallback((text: string) => {
      valueRef.current = text;
      setValue(text);
    }, []);

    const resetField = useCallback(() => {
      cancelPredictionsRef.current?.();
      setFieldValue('');
      setQuery('');
      setHasPickedPrediction(false);
      setManualAddress('');
    }, [setFieldValue, setManualAddress]);

    const clearField = useCallback(() => {
      resetField();
      setIsFocused(false);
      inputRef.current?.blur();
      onBlur?.('');
    }, [onBlur, resetField]);

    useImperativeHandle(
      forwardedRef,
      () => ({
        focus: () => inputRef.current?.focus(),
        blur: () => inputRef.current?.blur(),
        clear: clearField,
      }),
      [clearField]
    );

    const onChangeText = useCallback(
      (text: string) => {
        setFieldValue(text);
        setHasPickedPrediction(false);
        cancelPredictionsRef.current = debounceQuery(text);
      },
      [debounceQuery, setFieldValue]
    );

    const onSelectPrediction = useCallback(
      (prediction: Prediction) => {
        const address = prediction.description;

        setFieldValue(address);
        setQuery(address);
        setHasPickedPrediction(true);
        updateManualCoordinates(address, prediction.placeId);
        logRBIEvent({
          name: CustomEventNames.MANUAL_LOCATION_SEARCH,
          type: EventTypes.Search,
          attributes: { searchInput: value, selectedAddress: address },
        });
        // The results the guest just asked for are behind the keyboard.
        Keyboard.dismiss();
        // Web has no keyboard to dismiss, so the field would otherwise stay active.
        inputRef.current?.blur();
        // Tab out of the field should land on the store the search produced.
        requestStoreLocatorFirstRestaurantFocus();
      },
      [logRBIEvent, setFieldValue, updateManualCoordinates, value]
    );

    const onClear = useCallback(() => {
      resetField();
      inputRef.current?.focus();
    }, [resetField]);

    const handleFocus = useCallback(() => {
      setIsFocused(true);
      onFocus?.();
    }, [onFocus]);

    const handleBlur = useCallback(() => {
      setIsFocused(false);
      onBlur?.(valueRef.current);
    }, [onBlur]);

    // Tab out of a field holding a chosen address jumps to the first result rather than
    // walking the whole map control cluster. React Native Web does not surface Tab through
    // `onKeyPress`, so the listener has to go on the DOM node.
    useEffect(() => {
      if (Platform.OS !== 'web') {
        return;
      }

      const node = inputRef.current as unknown as HTMLInputElement | null;
      if (!node) {
        return;
      }

      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Tab' || event.shiftKey || !hasPickedPrediction) {
          return;
        }

        if (focusStoreLocatorFirstRestaurantResult()) {
          event.preventDefault();
        }
      };

      node.addEventListener('keydown', onKeyDown);

      return () => node.removeEventListener('keydown', onKeyDown);
    }, [hasPickedPrediction]);

    const fieldLabel = placeholder ?? formatMessage({ id: 'enterYourDeliveryAddress' });
    // Keep the list up after the input blurs. On web the field loses focus on mousedown of a
    // suggestion, and unmounting here would drop the click. The previous locator field did the
    // same: it only closed the list after a pick, a clear, or the query getting too short.
    const showPredictions = query.trim().length >= MIN_QUERY_LENGTH && !hasPickedPrediction;
    const labelledByNative = addressHeadingNativeId && Platform.OS !== 'web';

    return (
      <View style={[styles.container, style]}>
        <View
          style={[
            styles.inputContainer,
            isFocused && styles.inputContainerFocused,
            inputContainerStyle,
          ]}
        >
          <RnTextInput
            ref={inputRef}
            testID={STORE_LOCATOR_SEARCH_INPUT}
            value={value}
            autoFocus={autoFocus}
            onChangeText={onChangeText}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={fieldLabel}
            placeholderTextColor={tokens.colors.$placeholderText}
            autoCorrect={false}
            returnKeyType="search"
            underlineColorAndroid="transparent"
            textAlignVertical="center"
            // Submitting has nothing left to do — a prediction has to be picked to move the
            // map — so the only useful response is to get the keyboard out of the way.
            onSubmitEditing={() => inputRef.current?.blur()}
            style={[styles.input, inputStyle]}
            accessibilityLabel={addressHeadingNativeId ? undefined : fieldLabel}
            {...(labelledByNative ? { accessibilityLabelledBy: addressHeadingNativeId } : {})}
            {...(addressHeadingNativeId && Platform.OS === 'web'
              ? ({ 'aria-labelledby': addressHeadingNativeId } as object)
              : {})}
          />

          {Boolean(value) && (
            <Pressable
              testID="store-locator-search-clear"
              accessibilityLabel={`${formatMessage({ id: 'clearText' })}. ${value}`}
              dd-action-name="store locator clear search"
              onPress={onClear}
              borderRadius={999}
              hitSlop={12}
              style={styles.clearButton}
            >
              <IconClose size={20} color={tokens.colors.$blackOpacity75} />
            </Pressable>
          )}
        </View>

        {showPredictions && (predictions.length > 0 || !isLoadingPredictions) && (
          <View
            style={[styles.dropdown, dropdownStyle]}
            accessibilityRole={Platform.OS === 'web' ? 'list' : undefined}
          >
            {predictions.length === 0 ? (
              <View style={styles.predictionRow}>
                <Text.Paragraph size="xs" style={styles.predictionSecondary}>
                  {formatMessage({ id: 'noLocationsMatchYourSearch' })}
                </Text.Paragraph>
              </View>
            ) : (
              predictions.map((prediction, index) => (
                <Pressable
                  key={prediction.placeId}
                  testID={STORE_LOCATOR_SUGGESTION_AT(index)}
                  accessibilityLabel={expandUSStateAbbreviationsInAddress(prediction.description)}
                  dd-action-name="store locator select suggestion"
                  onPress={() => onSelectPrediction(prediction)}
                  borderRadius={0}
                  style={[styles.predictionRow, index > 0 && styles.predictionRowDivided]}
                >
                  <Text.Paragraph
                    size="sm"
                    weight="semibold"
                    numberOfLines={1}
                    style={styles.predictionMain}
                  >
                    {prediction.mainText}
                  </Text.Paragraph>
                  <Text.Paragraph size="xs" numberOfLines={1} style={styles.predictionSecondary}>
                    {prediction.secondaryText}
                  </Text.Paragraph>
                </Pressable>
              ))
            )}
          </View>
        )}
      </View>
    );
  }
);

const useLocationSearchBarMqStyles = createMqStyles({
  container: {
    $base: {
      overflow: 'visible',
      position: 'relative',
    },
  },
  // Figma: white fill, 1pt Black 30% stroke, 4pt radius, 48pt tall, 16pt inset.
  inputContainer: {
    $base: {
      alignItems: 'center',
      backgroundColor: tokens.colors.$white,
      borderColor: tokens.colors.$blackOpacity30,
      borderRadius: 4,
      borderWidth: 1,
      flexDirection: 'row',
      height: 48,
      paddingHorizontal: 16,
    },
  },
  inputContainerFocused: {
    $base: {
      borderColor: tokens.colors.$houseDark,
    },
  },
  input: {
    $base: {
      color: tokens.colors.$houseDark,
      flex: 1,
      fontFamily: 'Montserrat',
      fontSize: 14,
      ...Platform.select({
        android: {
          // lineHeight plus the default EditText padding pushes the glyphs
          // outside the clip bounds, so the field looks empty while typing.
          paddingVertical: 0,
        },
        default: { lineHeight: 20 },
        web: { lineHeight: 20, outlineWidth: 0 },
      }),
    },
  },
  clearButton: {
    $base: {
      alignItems: 'center',
      height: 24,
      justifyContent: 'center',
      width: 24,
    },
  },
  // Floats over whatever follows the field — on desktop that is the tabs and the store list,
  // which must not shift down as the guest types.
  dropdown: {
    $base: {
      backgroundColor: tokens.colors.$white,
      borderRadius: 4,
      elevation: 8,
      left: 0,
      // Figma all but joins the list to the field — 2pt under it on mobile, 4pt on desktop.
      marginTop: 2,
      overflow: 'hidden',
      position: 'absolute',
      right: 0,
      shadowColor: tokens.colors.$black,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.15,
      shadowRadius: 6,
      top: '100%',
      zIndex: 3,
    },
    $gteDesktop: {
      marginTop: 4,
    },
  },
  predictionRow: {
    $base: {
      alignItems: 'flex-start',
      backgroundColor: tokens.colors.$white,
      gap: 3,
      paddingHorizontal: 24,
      paddingVertical: 8,
    },
    $gteDesktop: {
      gap: 5,
    },
  },
  predictionRowDivided: {
    $base: {
      borderTopColor: tokens.colors.$black10,
      borderTopWidth: 1,
    },
  },
  predictionMain: {
    $base: {
      color: tokens.colors.$houseDark,
    },
  },
  predictionSecondary: {
    $base: {
      color: tokens.colors.$blackOpacity75,
    },
  },
});
